import { supabase } from './supabase';

// Email service that calls Supabase Edge Functions
const emailService = {
  /**
   * Check if email service is properly configured
   */
  isConfigured: () => {
    return Boolean(supabase);
  },

  /**
   * Send email via Supabase Edge Function with retry logic
   */
  sendEmailWithRetry: async (emailType, emailData, retries = 2) => {
    let lastError = null;
    
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        const result = await emailService.sendEmail(emailType, emailData);
        
        if (result.success) {
          return result;
        }
        
        lastError = result.error;
        
        // Don't retry on certain errors
        if (result.error?.includes('not found') || result.error?.includes('CORS')) {
          return result;
        }
        
        // Wait before retry with exponential backoff
        if (attempt < retries) {
          await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
        }
      } catch (error) {
        lastError = error.message;
        
        if (attempt < retries) {
          await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
        }
      }
    }
    
    return { success: false, error: lastError || 'Email sending failed after retries' };
  },

  /**
   * Send email via Supabase Edge Function
   */
  sendEmail: async (emailType, emailData) => {
    try {
      const { data, error } = await supabase.functions.invoke('send-email', {
        body: {
          emailType,
          ...emailData
        }
      });

      if (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Supabase function error:', error);
        }
        // Check for common errors
        if (error.message.includes('not found') || error.message.includes('404')) {
          return { 
            success: false, 
            error: 'Email service not available. Please check if the Edge Function is deployed.' 
          };
        }
        if (error.message.includes('CORS')) {
          return { 
            success: false, 
            error: 'CORS policy error. Please check your Supabase CORS configuration.' 
          };
        }
        return { success: false, error: error.message };
      }

      if (!data || !data.success) {
        const errorMsg = data?.error || 'Unknown email service error';
        console.error('Email service error:', errorMsg);
        return { success: false, error: errorMsg };
      }

      return { success: true, data: data.data };
    } catch (error) {
      console.error('Error calling email service:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Send verification email to new users
   * With checks to prevent duplicate emails
   */
  sendVerificationEmail: async (email, verificationUrl, fullName, userRole) => {
    try {
      // Extract user ID from verificationUrl to check if we've sent an email already
      const urlParams = new URL(verificationUrl);
      const token = urlParams.searchParams.get('token');
      
      if (token) {
        // Check if we've already sent a verification email recently
        const { data } = await supabase
          .from('user_profiles')
          .select('verification_email_sent, verification_email_sent_at')
          .eq('id', token)
          .maybeSingle();
          
        // If we've sent an email in the last 5 minutes, don't send another one
        if (data?.verification_email_sent && data?.verification_email_sent_at) {
          const sentAt = new Date(data.verification_email_sent_at);
          const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
          
          if (sentAt > fiveMinutesAgo) {
            return { 
              success: true, 
              data: { skipped: true, message: 'Verification email already sent recently' } 
            };
          }
        }
      }
      
      // If we get here, either we haven't sent an email recently or we couldn't check
      return await emailService.sendEmail('verification', {
        email,
        verificationUrl,
        fullName,
        userRole
      });
    } catch (error) {
      console.error('Error in sendVerificationEmail:', error);
      // Fall back to the original behavior if our check fails
      return await emailService.sendEmail('verification', {
        email,
        verificationUrl,
        fullName,
        userRole
      });
    }
  },

  /**
   * Send password reset email
   * Note: Duplicate email checking is now handled in authService.resetPassword()
   */
  sendPasswordResetEmail: async (email, resetUrl, fullName) => {
    try {
      // Send the password reset email directly
      // Rate limiting and duplicate checking is handled upstream in authService
      return await emailService.sendEmail('password-reset', {
        email,
        resetUrl,
        fullName
      });
    } catch (error) {
      console.error('Error in sendPasswordResetEmail:', error);
      return { 
        success: false, 
        error: error.message || 'Failed to send password reset email'
      };
    }
  },

  /**
   * Send welcome email after successful verification
   */
  sendWelcomeEmail: async (email, fullName, userRole) => {
    return await emailService.sendEmail('welcome', {
      email,
      fullName,
      userRole
    });
  },

  /**
   * Send login notification email
   */
  sendLoginNotification: async (email, fullName, loginDetails) => {
    return await emailService.sendEmail('login-notification', {
      email,
      fullName,
      loginDetails
    });
  },

  /**
   * Send password change confirmation email
   */
  sendPasswordChangeConfirmation: async (email, fullName, userRole) => {
    return await emailService.sendEmail('password-change', {
      email,
      fullName,
      userRole
    });
  },
  
  /**
   * Send interview scheduled notification email
   */
  sendInterviewScheduledNotification: async (recipientEmail, recipientName, interviewDetails) => {
    return await emailService.sendEmail('interview-scheduled', {
      email: recipientEmail,
      fullName: recipientName,
      ...interviewDetails
    });
  },

  /**
   * Send admin invitation email
   */
  sendAdminInvitation: async (email, inviterName, inviterEmail, signupUrl) => {
    return await emailService.sendEmail('admin-invitation', {
      email,
      inviterName,
      inviterEmail,
      signupUrl,
      platformName: 'VA Sprouts'
    });
  },

  /**
   * Send moderator invitation email
   */
  sendModeratorInvitation: async (email, inviterName, inviterEmail, signupUrl) => {
    return await emailService.sendEmail('moderator-invitation', {
      email,
      inviterName,
      inviterEmail,
      signupUrl,
      platformName: 'VA Sprouts'
    });
  },

  /**
   * Send job approval notification email
   */
  sendJobApprovalEmail: async (email, fullName, companyName, jobDetails) => {
    return await emailService.sendEmail('job-approved', {
      email,
      fullName,
      companyName,
      jobTitle: jobDetails.title,
      jobId: jobDetails.id,
      platformName: 'VA Sprouts'
    });
  },

  /**
   * Send job rejection notification email
   */
  sendJobRejectionEmail: async (email, fullName, companyName, jobDetails, rejectionReason) => {
    return await emailService.sendEmail('job-rejected', {
      email,
      fullName,
      companyName,
      jobTitle: jobDetails.title,
      jobId: jobDetails.id,
      rejectionReason,
      platformName: 'VA Sprouts'
    });
  },

  /**
   * Send subscription-related email
   */
  sendSubscriptionEmail: async (type, userEmail, userName, planName, extraData = {}) => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log(`[emailService] Sending subscription ${type} email:`, {
          email: userEmail,
          plan: planName,
          ...extraData
        });
      }

      return await emailService.sendEmailWithRetry(`subscription-${type}`, {
        email: userEmail,
        fullName: userName,
        planName,
        date: new Date().toLocaleDateString(),
        ...extraData
      });
    } catch (error) {
      console.error(`Error sending subscription ${type} email:`, error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Send subscription confirmation email
   */
  sendSubscriptionConfirmation: async (userEmail, userName, planName, amount, billingCycle) => {
    return await emailService.sendSubscriptionEmail('confirmed', userEmail, userName, planName, {
      amount,
      billingCycle
    });
  },

  /**
   * Send subscription cancellation email
   */
  sendCancellationEmail: async (userEmail, userName, planName, accessUntil) => {
    return await emailService.sendSubscriptionEmail('cancelled', userEmail, userName, planName, {
      accessUntil
    });
  },

  /**
   * Send subscription expiration email
   */
  sendExpirationEmail: async (userEmail, userName, planName) => {
    return await emailService.sendSubscriptionEmail('expired', userEmail, userName, planName);
  },

  /**
   * Send subscription renewal reminder email
   */
  sendRenewalReminderEmail: async (userEmail, userName, planName, renewalDate) => {
    return await emailService.sendSubscriptionEmail('renewal-reminder', userEmail, userName, planName, {
      renewalDate
    });
  },

  /**
   * Send marketing campaign email
   */
  sendMarketingEmail: async ({ to, subject, htmlContent }) => {
    try {
      return await emailService.sendEmail('marketing_campaign', {
        to,
        subject,
        htmlContent
      });
    } catch (error) {
      console.error('Error sending marketing email:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Send account deletion confirmation email
   */
  sendAccountDeletedEmail: async (email, fullName, userRole) => {
    return await emailService.sendEmail('account-deleted', {
      email,
      fullName,
      userRole
    });
  }
};

export default emailService;
